import React, { FC, useCallback } from 'react'
import { ConfirmationModal, IModalBaseProps } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useSavedLaneMutations } from 'apps/fleet-planner/hooks/useSavedLaneMutations'

interface ISavedLanesDeleteModalProps
  extends Omit<IModalBaseProps, 'modalType' | 'open' | 'actions' | 'size' | 'title'> {
  savedLaneId: number
  onSuccess: () => void
}
export const SavedLanesDeleteModal: FC<ISavedLanesDeleteModalProps> = ({
  onClose,
  onSuccess,
  savedLaneId,
}) => {
  const { deleteSavedLane, loading } = useSavedLaneMutations()
  const { openToast } = useGlobalToast()
  const { trackEvent } = useFMSAnalytics()

  const showSuccessToast = useCallback(() => {
    openToast({
      toastType: 'default',
      toastMessage: `Lane deleted successfully`,
    })
  }, [openToast])

  const showErrorToast = useCallback(() => {
    openToast({
      toastType: 'alert',
      toastMessage: `Server error. Contact tech support for assistance. Retry`,
    })
  }, [openToast])

  const onDeleteConfirmHandler = useCallback(async () => {
    try {
      const response = await deleteSavedLane(savedLaneId)
      if (response) {
        trackEvent('Saved Lanes', 'DELETED_SAVED_LANE')
        showSuccessToast()
        onSuccess()
      } else {
        showErrorToast()
      }
    } catch (e) {
      showErrorToast()
    }
  }, [deleteSavedLane, savedLaneId, trackEvent, showSuccessToast, onSuccess, showErrorToast])

  return (
    <ConfirmationModal
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: loading,
          action: onClose,
        },
        {
          label: 'Delete Saved Lane',
          type: 'confirm',
          disabled: loading,
          action: onDeleteConfirmHandler,
          dataTest: 'saved-lane-delete-button',
        },
      ]}
      title='Delete Saved Lane'
      size={3}
      open>
      Are you sure you would like to delete this saved lane? By deleting you will no longer receive
      any personalized recommendations based on this lane.
    </ConfirmationModal>
  )
}

export default SavedLanesDeleteModal
